import { format, formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowDown, ArrowUp, Gift, Zap, RefreshCw, Wrench } from "lucide-react";
import { useCreditTransactions } from "@/hooks/useCreditTransactions";
import type { CreditTransactionSource } from "@/lib/credit-types";

const getSourceInfo = (source: CreditTransactionSource) => {
  switch (source) {
    case "monthly_grant":
      return { label: "Monthly Grant", icon: Gift, color: "text-reqqy-success" };
    case "topup_purchase":
      return { label: "Top-up Purchase", icon: ArrowUp, color: "text-primary" };
    case "run_consumption":
      return { label: "Analysis Run", icon: Zap, color: "text-reqqy-warning" };
    case "refund":
      return { label: "Refund", icon: RefreshCw, color: "text-primary" };
    case "admin_adjustment":
      return { label: "Adjustment", icon: Wrench, color: "text-muted-foreground" };
    default:
      return { label: source, icon: Zap, color: "text-muted-foreground" };
  }
};

export function CreditTransactionHistory() {
  const { transactions, loading } = useCreditTransactions(15);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No credit transactions yet</p>
        <p className="text-sm">Your credit usage history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {transactions.map((transaction) => {
        const sourceInfo = getSourceInfo(transaction.source);
        const Icon = sourceInfo.icon;
        const isPositive = transaction.amount > 0;

        return (
          <div
            key={transaction.id}
            className="flex items-center gap-4 p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
          >
            <div className={`p-2 rounded-lg bg-muted ${sourceInfo.color}`}>
              <Icon className="w-4 h-4" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{sourceInfo.label}</p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(transaction.createdAt), { addSuffix: true })}
              </p>
            </div>

            <div className="text-right">
              <p className={`font-semibold ${isPositive ? "text-reqqy-success" : "text-foreground"}`}>
                {isPositive ? "+" : ""}{transaction.amount} credits
              </p>
              <p className="text-xs text-muted-foreground">
                Balance: {transaction.balanceAfter}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
